import {
  SERVE_DEFAULTS,
  ageBucket,
  ageSeconds,
  expoAgeAnchor,
  formatMinor,
  readyForLabel,
  remainingMinor,
  serveCorrectionMode,
  serveOffer,
  isSettled,
} from '@didto/domain';
import type { AgeThresholds } from '@didto/domain';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Loading,
  Notice,
  PageHeader,
  Select,
  Sheet,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useDirectActions } from '@/app/directActions';
import type { ActionOutcome, PendingAction } from '@/app/directActions';
import { useSession } from '@/app/session';
import { useToast } from '@/app/toast';
import { useClock } from '@/app/useClock';
import { useSetting } from '@/app/useSetting';
import { errorText } from '@/lib/errors';
import {
  expoItems,
  expoKeys,
  fetchExpoBoard,
  fetchRecentlyServed,
  fetchToSettle,
  orderTickets,
} from '@/lib/expo';
import type { ExpoOrder, ExpoTicket, ServedTicket } from '@/lib/expo';
import { useOnline } from '@/lib/network';
import { ORDER_TYPE_LABEL, fetchOpenOrders, orderKeys } from '@/lib/orders';
import { topicPos, useChannelState, useRealtimeInvalidate } from '@/lib/realtime';
import { ORDER_STATUS_TONE, PAYMENT_STATUS_TONE, fulfilmentTone } from '@/lib/status';
import { sessionKeys } from '@/lib/tableSessions';
import { useWakeLock } from '../kds/kdsSupport';
import {
  AgeClock,
  LivePill,
  OrderNo,
  StateBadge,
  TicketState,
  Unconfirmed,
  clockTime,
  lineMeta,
} from '../kds/live';
import { setTerminalChoice, usePosContext, useStations, useTables } from '../pos/posData';
import { ApprovalSheet, ReasonSheet } from '../pos/sheets';
import type { ApprovalRequest, ReasonRequest } from '../pos/sheets';
import { ForceClearSheet, TableOccupancyList } from '../tables/TableOccupancy';
import { useOpenSessions, useTableClear } from '../tables/tableSupport';
import { serveCorrection } from './serveCorrection';
import './expo.css';

/**
 * Expo / pass board (Phase 6, ADR-021). An enrolled Expo terminal with a staff PIN session is the
 * operational surface; a manager or owner previews it from a browser and may serve only after
 * naming the terminal they act from. Queries are the truth (polled every 15 s, refetched on focus
 * and reconnect); realtime hints only make them refetch sooner. Every serve goes straight to the
 * server with a fixed command id and an explicit retry, never through a queue; while the server
 * cannot be reached nothing can be served or taken back (ADR-037).
 */
const INVALIDATE: readonly (readonly string[])[] = [['expo'], ['orders'], ['table-sessions']];

export function ExpoPage() {
  const s = useSession();
  const ctx = usePosContext();
  const { toast } = useToast();
  const online = useOnline();
  const now = useClock(1000);
  const isDevice = ctx.principal === 'device';
  const locationId = ctx.locationId;
  const canServe = s.can('serve.operate');
  const canCorrect = s.can('serve.correct');
  const canClear = s.can('table.clear');
  /** A browser session acts only with a terminal context (owner rule: no unbound serve). */
  const canAct = canServe && (isDevice || !!ctx.terminal);
  const employeeName = isDevice
    ? (s.me?.terminal_session?.employee_display_name ?? '')
    : (s.me?.employee?.display_name ?? '');

  const warn = Number(useSetting('expo.warn_seconds'));
  const late = Number(useSetting('expo.late_seconds'));
  const undoWindow = Number(useSetting('serve.undo_window_seconds'));
  const thresholds: AgeThresholds = { warnSeconds: warn, lateSeconds: Math.max(late, warn) };

  const poll = SERVE_DEFAULTS.expoPollSeconds * 1000;
  const board = useQuery({
    queryKey: expoKeys.board(locationId),
    enabled: !!locationId,
    queryFn: () => fetchExpoBoard(locationId),
    refetchInterval: poll,
  });
  const settle = useQuery({
    queryKey: expoKeys.settle(locationId),
    enabled: !!locationId,
    queryFn: () => fetchToSettle(locationId),
    refetchInterval: poll,
  });
  const recent = useQuery({
    queryKey: expoKeys.recent(locationId),
    enabled: !!locationId,
    queryFn: () => fetchRecentlyServed(locationId),
    refetchInterval: poll,
  });
  const openOrders = useQuery({
    queryKey: orderKeys.open(locationId),
    enabled: !!locationId,
    queryFn: () => fetchOpenOrders(locationId),
    refetchInterval: 30_000,
  });
  const tables = useTables(locationId);
  const stations = useStations(locationId);
  const sessions = useOpenSessions(locationId);
  const tableClear = useTableClear(isDevice ? null : locationId, s.can('table.force_clear'));

  const topic = locationId ? topicPos(locationId) : null;
  useRealtimeInvalidate(topic, [expoKeys.all, sessionKeys.all, orderKeys.all]);
  const channel = useChannelState(topic);
  const awake = useWakeLock(true);
  const actions = useDirectActions(isDevice ? null : locationId, INVALIDATE);

  const [reason, setReason] = useState<ReasonRequest | null>(null);
  const [approval, setApproval] = useState<ApprovalRequest | null>(null);
  const [showRecent, setShowRecent] = useState(false);
  const [showTables, setShowTables] = useState(false);

  const tableName = (id: string | null) =>
    id ? ((tables.data ?? []).find((x) => x.id === id)?.name ?? 'table') : null;
  const stationName = (id: string) =>
    (stations.data ?? []).find((x) => x.id === id)?.name ?? 'station';
  const terminalPayload = () => (!isDevice && ctx.terminal ? { terminal_id: ctx.terminal.id } : {});

  /* ── serve ── */
  const afterServe = (r: ActionOutcome, what: string) => {
    if (!r.ok) return;
    const order = (r.result.order ?? {}) as { status?: string; fulfilment_status?: string };
    toast(
      order.status === 'completed'
        ? `${what} served; order completed`
        : order.fulfilment_status === 'served'
          ? `${what} served; order to settle`
          : `${what} served`,
      'success',
    );
  };
  const serveTicket = (t: ExpoTicket, label: string) =>
    void actions
      .run('ticket.serve', { ticket_id: t.id, ...terminalPayload() }, t.id, 'Serve')
      .then((r) => afterServe(r, label));
  const serveAll = (o: ExpoOrder) =>
    void actions
      .run('order.serve', { order_id: o.id, ...terminalPayload() }, o.id, 'Serve all')
      .then((r) => afterServe(r, `Order ${o.order_number}`));

  /* ── undo and correction (owner decision 5, ADR-022: the cashier or the owner approves) ── */
  const correct = serveCorrection({
    run: actions.run,
    toast,
    setReason,
    setApproval,
    terminalPayload: terminalPayload(),
    isDevice,
    canCorrect,
    undoWindow,
    onDone: () => setShowRecent(false),
  });
  const correction = (t: { id: string; served_at: string | null }, label: string) =>
    correct(t, label, now);

  /* ── presentation ── */
  const orders = [...(board.data ?? [])].sort((a, b) => {
    const aa = expoAgeAnchor(orderTickets(a));
    const bb = expoAgeAnchor(orderTickets(b));
    if (aa && bb) return aa.localeCompare(bb);
    if (aa) return -1;
    if (bb) return 1;
    return a.placed_at.localeCompare(b.placed_at);
  });
  const toSettle = settle.data ?? [];
  const recentTickets = recent.data ?? [];
  const refreshed = board.dataUpdatedAt
    ? clockTime(new Date(board.dataUpdatedAt).toISOString(), ctx.timeZone, true)
    : '—';
  const expoTerminals = ctx.terminals.filter((t) => t.kind === 'expo' || t.kind === 'pos');
  const cardProps = {
    now,
    thresholds,
    timeZone: ctx.timeZone,
    tableName,
    stationName,
    pending: actions.pending,
    canAct,
    online,
    undoWindow,
    canCorrect,
    onServe: serveTicket,
    onServeAll: serveAll,
    onCorrect: correction,
    onRetry: actions.retry,
    onDiscard: actions.discard,
  };

  return (
    <div className="d-page d-page--wide live-board expo" data-testid="expo">
      <PageHeader
        eyebrow={
          isDevice && ctx.terminal ? `${ctx.terminal.code} · ${ctx.terminal.name}` : undefined
        }
        title="Expo"
        actions={
          <>
            {!isDevice && (
              <Select
                value={ctx.terminal?.id ?? ''}
                onChange={(e) => setTerminalChoice(locationId, e.target.value || null)}
                aria-label="Terminal"
                data-testid="expo-terminal"
              >
                <option value="">View only: choose a terminal to serve…</option>
                {expoTerminals.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.code} · {t.name}
                  </option>
                ))}
              </Select>
            )}
            <span className="live-meta">
              <LivePill status={channel} online={online} testId="expo-connection" />
              <span className="d-num" data-testid="expo-refreshed">
                refreshed {refreshed}
              </span>
              {awake && <span title="The screen stays awake while this board is open">awake</span>}
            </span>
            <Button
              variant="ghost"
              onClick={() => setShowRecent(true)}
              data-testid="expo-recent-open"
            >
              Recent ({recentTickets.length})
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowTables(true)}
              data-testid="expo-tables-open"
            >
              Tables ({(sessions.data ?? []).length} occupied)
            </Button>
            {employeeName && <span className="d-faint d-break">{employeeName}</span>}
          </>
        }
      />

      {!canServe && (
        <Notice tone="warning" role="status">
          View only: your role has no serve.operate permission.
        </Notice>
      )}
      {canServe && !isDevice && !ctx.terminal && (
        <Notice tone="warning" role="status" testId="expo-unbound">
          View only: choose the Expo or POS terminal you are serving from to enable serving.
        </Notice>
      )}
      {board.error && (
        <ErrorState
          title={board.data ? 'Could not refresh the pass' : 'Could not load the pass'}
          message={errorText(board.error)}
          onRetry={() => void board.refetch()}
        />
      )}
      {toSettle.length > 0 && (
        <Notice
          tone="warning"
          role="status"
          testId="expo-settle"
          title={
            <span className="expo-settle__title">
              <span className="live-mark live-mark--alert" aria-hidden />
              To settle
            </span>
          }
        >
          <div className="expo-settle__list">
            {toSettle.map((o) => (
              <span key={o.id} className="expo-settle__chip">
                <span className="d-mono">{o.order_number}</span>
                {o.dining_table_id ? ` · ${tableName(o.dining_table_id)}` : ''}
                {o.customer_name ? ` · ${o.customer_name}` : ''} ·{' '}
                <span className="d-mono">
                  {formatMinor(
                    remainingMinor({
                      totalMinor: o.total_minor,
                      paidMinor: o.paid_minor,
                      refundedMinor: o.refunded_minor,
                      writtenOffMinor: o.written_off_minor,
                    }),
                    ctx.currency,
                  )}
                </span>
              </span>
            ))}
          </div>
        </Notice>
      )}
      {board.isLoading && <Loading label="Loading the pass…" />}

      <div className="live-grid" data-testid="expo-grid">
        {orders.map((o) => (
          <OrderCard key={o.id} o={o} {...cardProps} />
        ))}
      </div>
      {board.data && orders.length === 0 && (
        <EmptyState title="Nothing at the pass" testId="expo-empty">
          Orders appear here as soon as a station marks them ready.
        </EmptyState>
      )}

      <Sheet open={showRecent} onClose={() => setShowRecent(false)} title="Recently served">
        <div className="d-list" data-testid="expo-recent">
          {recentTickets.length === 0 && (
            <EmptyState
              title={`Nothing served in the last ${SERVE_DEFAULTS.recentWindowMinutes / 60} hours.`}
            />
          )}
          {recentTickets.map((t) => (
            <ServedRow
              key={t.id}
              t={t}
              now={now}
              timeZone={ctx.timeZone}
              tableName={tableName}
              stationName={stationName}
              pending={actions.pending[t.id]}
              canAct={canAct}
              online={online}
              undoWindow={undoWindow}
              canCorrect={canCorrect}
              onCorrect={correction}
              onRetry={actions.retry}
              onDiscard={actions.discard}
            />
          ))}
        </div>
      </Sheet>

      <Sheet open={showTables} onClose={() => setShowTables(false)} title="Tables">
        <TableOccupancyList
          tables={tables.data ?? []}
          sessions={sessions.data ?? []}
          orders={openOrders.data ?? []}
          currency={ctx.currency}
          timeZone={ctx.timeZone}
          canClear={canClear}
          busy={tableClear.busy}
          onClear={(t) => void tableClear.clear(t)}
        />
      </Sheet>
      <ForceClearSheet
        request={tableClear.force}
        busy={!!tableClear.busy}
        onConfirm={(why) => void tableClear.forceClear(why)}
        onClose={tableClear.cancel}
      />
      <ReasonSheet request={reason} onClose={() => setReason(null)} />
      <ApprovalSheet request={approval} onClose={() => setApproval(null)} />
    </div>
  );
}

interface CardCallbacks {
  onServe: (t: ExpoTicket, label: string) => void;
  onServeAll: (o: ExpoOrder) => void;
  onCorrect: (t: { id: string; served_at: string | null }, label: string) => void;
  onRetry: (target: string) => void;
  onDiscard: (target: string) => void;
}

function OrderCard({
  o,
  now,
  thresholds,
  timeZone,
  tableName,
  stationName,
  pending,
  canAct,
  online,
  undoWindow,
  canCorrect,
  onServe,
  onServeAll,
  onCorrect,
  onRetry,
  onDiscard,
}: {
  o: ExpoOrder;
  now: number;
  thresholds: AgeThresholds;
  timeZone: string;
  tableName: (id: string | null) => string | null;
  stationName: (id: string) => string;
  pending: Record<string, PendingAction>;
  canAct: boolean;
  online: boolean;
  undoWindow: number;
  canCorrect: boolean;
} & CardCallbacks) {
  const tickets = orderTickets(o);
  const anchor = expoAgeAnchor(tickets);
  const bucket = anchor ? ageBucket(anchor, now, thresholds) : null;
  const offer = serveOffer(tickets);
  const op = pending[o.id];
  const busy = !!op && op.state === 'sending';
  const cls = [
    'live-card',
    'expo-card',
    anchor ? 'live-card--ready' : 'live-card--waiting',
    bucket === 'warn' && 'live-card--warn',
    bucket === 'late' && 'live-card--late',
  ]
    .filter(Boolean)
    .join(' ');
  const scheduled =
    o.type === 'advance' && o.scheduled_for ? clockTime(o.scheduled_for, timeZone) : null;
  const table = o.dining_table_id ? tableName(o.dining_table_id) : null;
  const unpaid = !isSettled(o.payment_status);
  const partlyServed = o.fulfilment_status === 'partially_served';
  return (
    <article
      className={cls}
      data-testid="expo-order"
      data-order={o.order_number}
      data-age={bucket ?? 'waiting'}
      data-fulfilment={o.fulfilment_status}
      aria-label={`Order ${o.order_number}`}
    >
      <div className="live-card__head">
        <div className="live-card__id">
          <OrderNo value={o.order_number} />
          <div className="live-card__where">
            <span className="live-card__type">{ORDER_TYPE_LABEL[o.type]}</span>
            {table ? ` · ${table}` : ''}
            {o.covers ? ` · ${o.covers} guest${o.covers === 1 ? '' : 's'}` : ''}
          </div>
        </div>
        {anchor && bucket && (
          <AgeClock seconds={ageSeconds(anchor, now)} bucket={bucket} testId="expo-age" />
        )}
      </div>
      <div className={`expo-card__state${anchor ? ' expo-card__state--ready' : ''}`}>
        <span className={`live-mark live-mark--${anchor ? 'full' : 'ring'}`} aria-hidden />
        <span className="expo-card__for" data-testid="expo-ready-for">
          {anchor ? readyForLabel(o.type) : 'WAITING ON THE REST'}
        </span>
      </div>
      {(o.customer_name || scheduled || unpaid || partlyServed) && (
        <div className="live-card__tags">
          {o.customer_name && <Badge>{o.customer_name}</Badge>}
          {scheduled && <Badge tone="info">for {scheduled}</Badge>}
          {unpaid && (
            <StateBadge tone={PAYMENT_STATUS_TONE.unpaid} mark="alert">
              unpaid
            </StateBadge>
          )}
          {partlyServed && (
            <StateBadge tone={fulfilmentTone('partially_served')} mark="half">
              partly served
            </StateBadge>
          )}
        </div>
      )}
      {o.notes && <div className="live-card__note">“{o.notes}”</div>}

      <div className="expo-stations">
        {tickets.map((t) => (
          <StationRow
            key={t.id}
            t={t}
            now={now}
            timeZone={timeZone}
            stationName={stationName}
            pending={pending[t.id]}
            canAct={canAct && !busy}
            online={online}
            undoWindow={undoWindow}
            canCorrect={canCorrect}
            onServe={onServe}
            onCorrect={onCorrect}
            onRetry={onRetry}
            onDiscard={onDiscard}
          />
        ))}
      </div>

      {op?.state === 'unconfirmed' && (
        <Unconfirmed
          label={op.label}
          prefix="expo"
          onRetry={() => onRetry(o.id)}
          onDiscard={() => onDiscard(o.id)}
        />
      )}
      {busy && (
        <div className="live-sending" role="status">
          <span className="d-spinner" aria-hidden />
          Sending {op.label.toLowerCase()}…
        </div>
      )}
      {canAct && offer === 'serve_all' && (
        <div className="live-card__foot">
          <Button
            size="lg"
            variant="primary"
            busy={busy}
            disabled={!online}
            onClick={() => onServeAll(o)}
            data-testid="expo-serve-all"
          >
            Serve all
          </Button>
        </div>
      )}
    </article>
  );
}

function StationRow({
  t,
  now,
  timeZone,
  stationName,
  pending,
  canAct,
  online,
  undoWindow,
  canCorrect,
  onServe,
  onCorrect,
  onRetry,
  onDiscard,
}: {
  t: ExpoTicket;
  now: number;
  timeZone: string;
  stationName: (id: string) => string;
  pending: PendingAction | undefined;
  canAct: boolean;
  online: boolean;
  undoWindow: number;
  canCorrect: boolean;
  onServe: (t: ExpoTicket, label: string) => void;
  onCorrect: (t: { id: string; served_at: string | null }, label: string) => void;
  onRetry: (target: string) => void;
  onDiscard: (target: string) => void;
}) {
  const items = expoItems(t);
  const sending = pending?.state === 'sending';
  const label = `${stationName(t.production_station_id)}${t.is_addition ? ' addition' : ''}`;
  const mode =
    t.status === 'served' ? serveCorrectionMode(t.served_at, now, undoWindow, canCorrect) : null;
  const serve = canAct && t.status === 'ready';
  const takeBack = canAct && !!mode;
  return (
    <div
      className={`expo-station expo-station--${t.status}`}
      data-testid="expo-station"
      data-status={t.status}
      data-station={t.production_station_id}
    >
      <div className="expo-station__head">
        <span className="expo-station__name">
          {stationName(t.production_station_id)}
          {t.is_addition ? ' · addition' : ''}
          <span className="d-faint d-mono"> #{t.ticket_seq}</span>
        </span>
        <span className="expo-station__state">
          <TicketState status={t.status} />
          {t.status === 'served' && t.served_at && (
            <span className="d-faint d-num">{clockTime(t.served_at, timeZone)}</span>
          )}
        </span>
      </div>
      <div className="expo-station__items">
        {items.map((it) => {
          const meta = lineMeta(it.order_item);
          return (
            <div
              key={it.id}
              className={`expo-station__item${it.status === 'cancelled' ? ' expo-station__item--cancelled' : ''}`}
            >
              <span className="expo-station__qty">{it.qty}×</span>
              <span className="expo-station__what">
                {it.order_item?.name_snapshot ?? 'Item'}
                {meta ? ` · ${meta}` : ''}
              </span>
              {it.status === 'cancelled' && (
                <StateBadge tone="danger" mark="cross">
                  cancelled
                </StateBadge>
              )}
            </div>
          );
        })}
      </div>
      {(serve || takeBack) && (
        <div className="expo-station__actions">
          {serve && (
            <Button
              size="lg"
              variant="primary"
              disabled={!online || sending}
              onClick={() => onServe(t, label)}
              data-testid="expo-serve"
              aria-label={`serve ${label}`}
            >
              Serve
            </Button>
          )}
          {takeBack && (
            <Button
              size="lg"
              variant="ghost"
              disabled={!online || sending}
              onClick={() => onCorrect(t, label)}
              data-testid={mode === 'undo' ? 'expo-undo' : 'expo-correct'}
              aria-label={`${mode === 'undo' ? 'undo' : 'correct'} ${label}`}
            >
              {mode === 'undo' ? 'Undo' : 'Correct…'}
            </Button>
          )}
        </div>
      )}
      {pending?.state === 'unconfirmed' && (
        <Unconfirmed
          label={pending.label}
          prefix="expo"
          onRetry={() => onRetry(t.id)}
          onDiscard={() => onDiscard(t.id)}
        />
      )}
    </div>
  );
}

function ServedRow({
  t,
  now,
  timeZone,
  tableName,
  stationName,
  pending,
  canAct,
  online,
  undoWindow,
  canCorrect,
  onCorrect,
  onRetry,
  onDiscard,
}: {
  t: ServedTicket;
  now: number;
  timeZone: string;
  tableName: (id: string | null) => string | null;
  stationName: (id: string) => string;
  pending: PendingAction | undefined;
  canAct: boolean;
  online: boolean;
  undoWindow: number;
  canCorrect: boolean;
  onCorrect: (t: { id: string; served_at: string | null }, label: string) => void;
  onRetry: (target: string) => void;
  onDiscard: (target: string) => void;
}) {
  const o = t.orders;
  const label = `${o?.order_number ?? 'order'} ${stationName(t.production_station_id)}`;
  const mode = serveCorrectionMode(t.served_at, now, undoWindow, canCorrect);
  const sending = pending?.state === 'sending';
  const completed = o?.status === 'completed';
  return (
    <div
      className="d-row"
      data-testid="expo-served"
      data-order={o?.order_number ?? ''}
      data-station={t.production_station_id}
    >
      <div className="d-row__main">
        <div className="d-row__title">
          <span className="d-mono">{o?.order_number ?? '…'}</span> ·{' '}
          {stationName(t.production_station_id)}
          {t.is_addition ? ' · addition' : ''}
        </div>
        <div className="d-row__meta">
          {o ? ORDER_TYPE_LABEL[o.type] : ''}
          {o?.dining_table_id ? ` · ${tableName(o.dining_table_id)}` : ''}
          {o?.customer_name ? ` · ${o.customer_name}` : ''}
          {t.served_at ? ` · served ${clockTime(t.served_at, timeZone)}` : ''}
        </div>
        {pending?.state === 'unconfirmed' && (
          <Unconfirmed
            label={pending.label}
            prefix="expo"
            onRetry={() => onRetry(t.id)}
            onDiscard={() => onDiscard(t.id)}
          />
        )}
      </div>
      {(completed || canAct) && (
        <div className="d-actions">
          {completed && (
            <StateBadge tone={ORDER_STATUS_TONE.completed} mark="check">
              completed
            </StateBadge>
          )}
          {canAct && (
            <Button
              variant="ghost"
              disabled={!online || sending}
              onClick={() => onCorrect(t, label)}
              data-testid={mode === 'undo' ? 'expo-recent-undo' : 'expo-recent-correct'}
            >
              {mode === 'undo' ? 'Undo' : 'Correct…'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
